import type { DpopKey } from './dpop-key-store';

/**
 * A DPoP proof (RFC 9449 section 4.2): a short JWT signed by the browser's key, fresh for every request.
 *
 * The gateway checks each claim against the request it actually received, so each has one exact form:
 *  - htm: the method in upper case. Axios reports methods in lower case.
 *  - htu: origin + path of the URL being called, with no query and no fragment. The request interceptor
 *    appends `?api-version=...` after the URL is built; that must not reach htu.
 *  - ath: base64url(SHA-256(access token)), present whenever a DPoP-bound token accompanies the proof,
 *    absent on the exchange call, where the token presented is the unbound B2C one.
 *
 * WebCrypto's ECDSA signature is already the raw r||s form JWS ES256 requires, so no DER conversion is
 * needed (the native app will need one; see the "DPoP on the Native App" doc).
 */
export async function createDpopProof(key: DpopKey, method: string, url: string, accessToken?: string): Promise<string> {
    const target = new URL(url, window.location.origin);
    const header = { typ: 'dpop+jwt', alg: 'ES256', jwk: key.publicJwk };
    const payload: Record<string, unknown> = {
        htm: method.toUpperCase(),
        htu: target.origin + target.pathname,
        iat: Math.floor(Date.now() / 1000),
        jti: randomId(),
    };
    if (accessToken) {
        payload.ath = base64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(accessToken)));
    }

    const signingInput = `${base64UrlJson(header)}.${base64UrlJson(payload)}`;
    const signature = await crypto.subtle.sign(
        { name: 'ECDSA', hash: 'SHA-256' },
        key.keyPair.privateKey,
        new TextEncoder().encode(signingInput),
    );
    return `${signingInput}.${base64Url(signature)}`;
}

function base64UrlJson(value: unknown): string {
    return base64Url(new TextEncoder().encode(JSON.stringify(value)));
}

function base64Url(data: ArrayBuffer | Uint8Array): string {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** A unique jti. randomUUID is missing from some older Safari builds, so fall back to 16 random bytes. */
function randomId(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return base64Url(crypto.getRandomValues(new Uint8Array(16)));
}
